// The audit log (A5U9, A12U11), owned by hand: a section of the Organization page; filters live in the URL; the loader
// warms the first page; behind `audit.read`.
import { ids } from '@orca/api-client/testIds'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AuditExport, AuditFilterBar, AuditLog } from '../../../../features/audit/AuditLog'
import { type AuditFilters, auditArea, auditQuery } from '../../../../features/audit/queries'
import '../../../../features/audit/strings'
import { AuditRetention } from '../../../../features/organization/AuditRetention'
import { OrgSection } from '../../../../features/organization/OrgSection'
import { accessQuery, can } from '../../../../shared/access/access'
import { text } from '../../../../shared/routing/search'
import { Part } from '../../../../shared/shell/StatePart'

export const Route = createFileRoute('/o/$orgSlug/organization/audit')({
  staticData: { screenId: 'AuditLogScreen', access: 'audit.read', ownHeader: true },
  // The route key's query arguments (screens.json `args`), as the apps' LinkResolver reads them;
  // unknown keys are dropped.
  validateSearch: (search: Record<string, unknown>): AuditFilters => ({
    actor: text(search.actor),
    action: text(search.action),
    area: auditArea(text(search.area)),
    q: text(search.q),
    from: text(search.from),
    to: text(search.to),
  }),
  loaderDeps: ({ search: { actor, action, area, q, from, to } }) => ({ actor, action, area, q, from, to }),
  loader: async ({ context: { queryClient, membership }, deps }) => {
    // Without audit.read the screen shows its refusal and asks for no entries: the access answer decides.
    const access = await queryClient.ensureQueryData(accessQuery(membership.orgId)).catch(() => null)
    if (can(access, 'audit.read')) {
      // Prefetched, never awaited to a throw: a failed read errors only its part (D193).
      await queryClient.prefetchInfiniteQuery(auditQuery(membership.orgId, deps))
    }
    return { orgId: membership.orgId }
  },
  component: AuditLogScreen,
})

function AuditLogScreen() {
  const { t } = useTranslation()
  const { orgId } = Route.useLoaderData()
  const { orgSlug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const filters: AuditFilters = {
    actor: search.actor,
    action: search.action,
    area: search.area,
    q: search.q,
    from: search.from,
    to: search.to,
  }
  return (
    <OrgSection
      orgId={orgId}
      section="audit"
      root={ids.auditLog.root}
      title={t('audit.title')}
      subtitle={t('organization.audit.subtitle')}
    >
      <Part quiet>
        <AuditFilterBar
          filters={filters}
          onChange={(patch) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })}
        />
      </Part>
      <AuditExport orgId={orgId} filters={filters} planHref={`/o/${orgSlug}/organization/plan`} />
      <Part testId={ids.auditLog.skeleton}>
        <AuditLog orgId={orgId} filters={filters} />
      </Part>
      <AuditRetention orgId={orgId} />
    </OrgSection>
  )
}
